import type { SituationCardSource, SituationSource } from "@peer-start/platform-contracts";
import { Button } from "@peer-start/ui";
import { Spinner } from "@peer-start/ui/reducate";
import { Library, Plus, RefreshCw, Trash2, Undo2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SituationConfirmDialog } from "./SituationConfirmDialog.js";
import { SourceFitBadge } from "./SourceFitBadge.js";
import { SourceReference } from "./SourceReference.js";
import { SituationSourceSummary } from "./SituationSourceSummary.js";
import { UnappliedSourcesNotice } from "./UnappliedSourcesNotice.js";
import { sourceContribution } from "../pages/situation-card-view.js";

export function SituationCardColumn({
  sources,
  removedSources,
  unappliedSources,
  slots,
  sourcesChanged,
  readOnly,
  pending,
  canGenerate,
  generating,
  showGenerate,
  onAcknowledgeUnapplied,
  onAddSources,
  onGenerate,
  onRemoveSource,
  onRestoreSource,
  showChanges,
}: {
  sources: readonly SituationCardSource[];
  removedSources: readonly SituationSource[];
  unappliedSources: readonly SituationSource[];
  slots: readonly { key: string; text: string | null; sourceIds: readonly string[] }[];
  sourcesChanged: boolean;
  readOnly: boolean;
  pending: boolean;
  canGenerate: boolean;
  generating: boolean;
  showGenerate: boolean;
  onAcknowledgeUnapplied: () => void;
  onAddSources: () => void;
  onGenerate: () => void;
  onRemoveSource: (sourceId: string) => void;
  onRestoreSource: (sourceId: string) => void;
  showChanges: boolean;
}) {
  const { t } = useTranslation();
  const [pendingRemoval, setPendingRemoval] = useState<SituationSource | null>(null);
  const activeIds = sources.map((source) => source.id);
  const removedIds = removedSources.map((source) => source.id);
  const sourceOrder = `${activeIds.join("|")}#${removedIds.join("|")}`;
  const [order, setOrder] = useState<string[]>(() => [...activeIds, ...removedIds]);
  useEffect(() => {
    const [active = "", dropped = ""] = sourceOrder.split("#");
    const nextIds = [...active.split("|"), ...dropped.split("|")].filter((id) => id.length > 0);
    setOrder((current) => {
      const kept = current.filter((id) => nextIds.includes(id));
      const added = nextIds.filter((id) => !current.includes(id));
      const next = [...added, ...kept];
      if (next.length === current.length && next.every((id, index) => id === current[index])) return current;
      return next;
    });
  }, [sourceOrder]);
  const removed = new Set(removedIds);
  const byId = new Map<string, SituationCardSource | SituationSource>([
    ...sources.map((source) => [source.id, source] as const),
    ...removedSources.map((source) => [source.id, source] as const),
  ]);
  const visibleIds = showChanges ? order : activeIds;
  const visible = visibleIds.flatMap((id) => {
    const source = byId.get(id);
    return source ? [source] : [];
  });
  const showList = sources.length > 0 || (showChanges && removedSources.length > 0);

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-background">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-4">
        {unappliedSources.length > 0 ? (
          <UnappliedSourcesNotice
            onAcknowledge={onAcknowledgeUnapplied}
            pending={pending}
            sources={unappliedSources}
          />
        ) : null}
        {showList ? (
          <div>
            <p className="text-sm text-secondary-text">{t("administration.situationCard.sourcesHint")}</p>
            <ul className="mt-3 divide-y divide-border">
              {visible.map((source, index) => {
                const isRemoved = removed.has(source.id);
                const isNew = showChanges && "isNew" in source && source.isNew;
                const contribution = isRemoved ? null : sourceContribution(source.id, slots, sourcesChanged);
                return (
                  <li className="flex items-center gap-3 py-2.5" key={source.id}>
                    <SourceReference number={index + 1} />
                    <SituationSourceSummary
                      excerpt={source.excerpt}
                      href={source.href}
                      id={source.id}
                      showHref={false}
                      status={
                        isRemoved
                          ? t("administration.situationCard.removedSource")
                          : isNew
                            ? t("administration.situationCard.newSource")
                            : undefined
                      }
                      struck={isRemoved}
                      fit={contribution ? <SourceFitBadge contribution={contribution} id={source.id} /> : null}
                      tag={source.tag}
                      title={source.title}
                      tone={isRemoved ? "danger" : isNew ? "success" : "muted"}
                    />
                    {readOnly ? null : isRemoved ? (
                      <Button
                        className="h-8! min-h-8! shrink-0 px-2.5! py-0! text-sm!"
                        controlId={`situation.sources.restore.${source.id}`}
                        disabled={pending}
                        onClick={() => onRestoreSource(source.id)}
                        type="button"
                        variant="secondary"
                      >
                        <Undo2 aria-hidden="true" className="size-4" />
                        {t("administration.situationCard.restoreSource")}
                      </Button>
                    ) : (
                      <button
                        aria-label={t("administration.situationCard.removeSourceNamed", {
                          title: source.title,
                        })}
                        className="grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:text-disabled disabled:hover:bg-transparent"
                        data-control-id={`situation.sources.remove.${source.id}`}
                        disabled={pending}
                        onClick={() => setPendingRemoval(source)}
                        type="button"
                      >
                        <Trash2 aria-hidden="true" className="size-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : unappliedSources.length > 0 ? null : (
          <div className="flex flex-1 flex-col items-center justify-center px-6 pb-8 text-center">
            <Library aria-hidden="true" className="size-8 text-muted-foreground" />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-secondary-text">
              {t("administration.situationCard.noCardSources")}
            </p>
          </div>
        )}
      </div>
      {showGenerate || !readOnly ? (
        <div className="shrink-0 space-y-2 bg-background px-4 pt-3 pb-4">
          {showGenerate ? (
            generating ? (
              <Button
                aria-busy="true"
                className="w-full"
                controlId="situation.detail.generate"
                disabled
                type="button"
                variant="secondary"
              >
                <Spinner color="current" label={t("administration.situationCard.generating")} size="xs" />
                {t("administration.situationCard.generating")}
              </Button>
            ) : (
              <Button
                className="w-full"
                controlId="situation.detail.generate"
                disabled={!canGenerate || pending}
                onClick={onGenerate}
                type="button"
                variant="secondary"
              >
                <RefreshCw aria-hidden="true" className="size-4" />
                {t("administration.situationCard.generate")}
              </Button>
            )
          ) : null}
          {readOnly ? null : (
            <Button
              className="w-full"
              controlId="situation.detail.add-sources"
              disabled={pending}
              onClick={onAddSources}
              type="button"
            >
              <Plus aria-hidden="true" className="size-4" />
              {t("administration.situationCard.addSources")}
            </Button>
          )}
        </div>
      ) : null}
      {pendingRemoval ? (
        <SituationConfirmDialog
          body={t("administration.situationCard.removeSourceBody", { title: pendingRemoval.title })}
          confirmLabel={t("administration.situationCard.removeSource")}
          destructive
          onCancel={() => setPendingRemoval(null)}
          onConfirm={() => {
            onRemoveSource(pendingRemoval.id);
            setPendingRemoval(null);
          }}
          pending={pending}
          title={t("administration.situationCard.removeSourceTitle")}
        />
      ) : null}
    </aside>
  );
}
